import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PencilSimple, FloppyDisk, X, ArrowCounterClockwise, LockKey, LockKeyOpen } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "el-porvenir-admin-key";

const ContentContext = createContext({
  values: {},
  unlocked: false,
  adminKey: "",
  setAdminKey: () => {},
  save: async () => {},
  revert: async () => {},
  openSignIn: () => {},
  signOut: () => {},
});

export function ContentProvider({ children }) {
  const [values, setValues] = useState({});
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORAGE) || "");
  const [unlocked, setUnlocked] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [draftKey, setDraftKey] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/content`);
      setValues(r.data || {});
    } catch (e) {
      console.error("content load failed", e);
    }
  }, []);

  const verifyKey = useCallback(async (key) => {
    if (!key) return false;
    try {
      await axios.get(`${API}/bigfoot-history/verify`, { headers: { "X-Admin-Key": key } });
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (adminKey) verifyKey(adminKey).then(setUnlocked);
    else setUnlocked(false);
  }, [adminKey, verifyKey]);

  const save = useCallback(async (key, value) => {
    if (!adminKey) {
      toast.error("Curator key required.");
      return false;
    }
    try {
      await axios.put(`${API}/content/${encodeURIComponent(key)}`, { value }, {
        headers: { "X-Admin-Key": adminKey },
      });
      setValues((prev) => ({ ...prev, [key]: value }));
      toast.success("Saved.");
      return true;
    } catch (e) {
      console.error(e);
      toast.error("Couldn't save — check your curator key.");
      return false;
    }
  }, [adminKey]);

  const revert = useCallback(async (key) => {
    if (!adminKey) return false;
    try {
      await axios.delete(`${API}/content/${encodeURIComponent(key)}`, {
        headers: { "X-Admin-Key": adminKey },
      });
      setValues((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      toast.success("Reverted to original.");
      return true;
    } catch (e) {
      console.error(e);
      toast.error("Revert failed.");
      return false;
    }
  }, [adminKey]);

  const openSignIn = () => setSignInOpen(true);
  const signOut = () => {
    localStorage.removeItem(KEY_STORAGE);
    setAdminKey("");
    setUnlocked(false);
    toast.message("Curator mode locked.");
  };

  const submitSignIn = async () => {
    const ok = await verifyKey(draftKey);
    if (ok) {
      localStorage.setItem(KEY_STORAGE, draftKey);
      setAdminKey(draftKey);
      setUnlocked(true);
      setSignInOpen(false);
      setDraftKey("");
      toast.success("Curator mode unlocked.");
    } else {
      toast.error("That key didn't open the lock.");
    }
  };

  return (
    <ContentContext.Provider value={{ values, unlocked, adminKey, setAdminKey, save, revert, openSignIn, signOut }}>
      {children}

      {/* Floating curator badge */}
      <div className="fixed bottom-5 left-5 z-40">
        {unlocked ? (
          <button
            onClick={signOut}
            data-testid="curator-badge-lock"
            className="flex items-center gap-2 bg-[#3A2E1A] text-[#F0E2C2] px-4 py-2 font-mono-label text-[10px] shadow-lg hover:bg-[#5C4A2A]"
          >
            <LockKeyOpen size={14} /> Curator · sign out
          </button>
        ) : (
          <button
            onClick={openSignIn}
            data-testid="curator-badge-unlock"
            className="flex items-center gap-2 bg-[#FAF8F5] text-[#2A3A2C] border border-[#D1C9B8] px-4 py-2 font-mono-label text-[10px] shadow-md hover:bg-[#F0E2C2]"
          >
            <LockKey size={14} /> Curator sign-in
          </button>
        )}
      </div>

      {signInOpen && (
        <div
          className="fixed inset-0 z-50 bg-[#2A3A2C]/40 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setSignInOpen(false)}
        >
          <div
            className="bg-[#FAF8F5] border border-[#D1C9B8] max-w-md w-full p-8 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="global-key-dialog"
          >
            <div className="flex items-center justify-between">
              <div className="font-mono-label text-[10px] text-[#5C6B5E]">Curator sign-in</div>
              <button onClick={() => setSignInOpen(false)} className="text-[#5C6B5E] hover:text-[#2A3A2C]" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <h3 className="font-serif-display text-3xl text-[#2A3A2C] mt-3">Enter your curator key.</h3>
            <p className="text-sm text-[#5C6B5E] mt-2">
              Unlocks inline editing of any prose across the site.
            </p>
            <input
              type="password"
              value={draftKey}
              onChange={(e) => setDraftKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitSignIn()}
              placeholder="••••••••"
              data-testid="global-key-input"
              autoFocus
              className="mt-6 w-full bg-transparent border-b border-[#D1C9B8] py-3 text-lg text-[#2A3A2C] focus:border-[#2A3A2C] outline-none"
            />
            <button
              onClick={submitSignIn}
              data-testid="global-key-submit"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#2A3A2C] text-[#F4F1EA] px-7 py-3 font-mono-label text-[11px] hover:bg-[#1A251C] transition-colors"
            >
              <LockKeyOpen size={14} /> Unlock
            </button>
          </div>
        </div>
      )}
    </ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext);
}

/**
 * Editable text block.
 * @param id      stable content key, e.g. "home.hero.title"
 * @param defaultValue  fallback text shown if no override exists
 * @param as      element to render ("span" | "p" | "h1" etc.) — default "span"
 * @param multiline   render as <textarea> when editing
 * @param className   pass-through styling
 */
export function Editable({ id, defaultValue, as: Tag = "span", multiline = false, className = "", children, ...rest }) {
  const { values, unlocked, save, revert } = useContent();
  const stored = values[id];
  const display = stored !== undefined ? stored : (defaultValue ?? children ?? "");

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(display);

  useEffect(() => { setDraft(display); }, [display]);

  if (editing) {
    return (
      <span className="inline-block w-full bg-[#FFF9E5] border border-dashed border-[#B79A60] p-2 align-top" data-testid={`editing-${id}`}>
        {multiline ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.max(3, Math.min(14, draft.split("\n").length + 1))}
            className="w-full bg-transparent outline-none resize-y text-inherit font-inherit leading-inherit"
            style={{ font: "inherit", lineHeight: "inherit", color: "inherit" }}
            autoFocus
          />
        ) : (
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full bg-transparent outline-none text-inherit"
            style={{ font: "inherit", lineHeight: "inherit", color: "inherit" }}
            autoFocus
          />
        )}
        <span className="block mt-2 flex gap-2 text-[10px] font-mono-label">
          <button
            onClick={async () => {
              const ok = await save(id, draft);
              if (ok) setEditing(false);
            }}
            className="inline-flex items-center gap-1 bg-[#2A3A2C] text-[#F4F1EA] px-2 py-1"
            data-testid={`save-${id}`}
          >
            <FloppyDisk size={12} /> Save
          </button>
          <button
            onClick={() => { setDraft(display); setEditing(false); }}
            className="inline-flex items-center gap-1 border border-[#D1C9B8] text-[#2A3A2C] px-2 py-1"
            data-testid={`cancel-${id}`}
          >
            <X size={12} /> Cancel
          </button>
          {stored !== undefined && (
            <button
              onClick={async () => {
                const ok = await revert(id);
                if (ok) setEditing(false);
              }}
              className="inline-flex items-center gap-1 border border-[#B75D3F] text-[#B75D3F] px-2 py-1"
              data-testid={`revert-${id}`}
            >
              <ArrowCounterClockwise size={12} /> Revert
            </button>
          )}
        </span>
      </span>
    );
  }

  const wrapperClass = `${className} ${unlocked ? "editable-wrapper" : ""}`.trim();

  return (
    <Tag className={wrapperClass} data-content-id={id} {...rest}>
      {display}
      {unlocked && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(true); }}
          className="editable-pencil"
          data-testid={`edit-${id}`}
          aria-label={`Edit ${id}`}
        >
          <PencilSimple size={12} />
        </button>
      )}
    </Tag>
  );
}
